import { requestWsTicket, sessionIdentityEpoch } from '../service/sessionProtocol';
import { readStoredAccessToken } from '../service/requestCredential';
// ws.ts
import { isBrowserOnline } from '../../../hooks/useOnlineStatus';

let ws: WebSocket | null = null;
let openGeneration = 0;
let opening = false;
let pingTimer: any = null;

// controle de reconexão
let reconnectTimer: any = null;
let reconnectAttempt = 0;
let shouldReconnect = true;

// guarda qual token foi usado na última conexão (pra reconectar quando mudar)
let lastToken: string | null = null;
let notificationNetworkListenersInstalled = false;

/**
 * Ticket efêmero de uso único, vinculado à sessão e ao endpoint.
 */

function installNotificationNetworkListeners(baseWsUrl: string): void {
  if (notificationNetworkListenersInstalled || typeof window === 'undefined') return;
  notificationNetworkListenersInstalled = true;

  window.addEventListener('offline', () => {
    shouldReconnect = false;
    clearTimers();
    if (ws) {
      try {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, 'Browser offline');
        }
      } catch {
        // ignore
      }
      ws = null;
    }
  });

  window.addEventListener('online', () => {
    if (!readStoredAccessToken()) return;
    shouldReconnect = true;
    connectNotificationsWS(baseWsUrl);
  });
}

/**
 * Tudo que chega aqui vai para os listeners:
 * - Mensagens vindas do WebSocket (NOTIFICATION, METRIC)
 * - Eventos locais do front (NOTIFICATION_UPDATED, etc)
 */
export type WsMessage =
  | { type: "NOTIFICATION"; company_id: string; ts?: string; payload?: any }
  | { type: "METRIC"; company_id: string; ts?: string; payload?: any }
  | { type: "NOTIFICATION_UPDATED"; company_id: string }
  | { type: "NOTIFICATION_CREATED"; company_id: string }
  | { type: string; [key: string]: any };

type WsListener = (msg: WsMessage) => void;

const listeners: WsListener[] = [];

export function addWsListener(fn: WsListener) {
  listeners.push(fn);
  return () => {
    const idx = listeners.indexOf(fn);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

/**
 * ✅ Evento local (sem backend) para quando você fizer like/dislike,
 * remover item do estado, atualizar badge etc.
 *
 * Exemplo:
 * emitLocalEvent({ type: "NOTIFICATION_UPDATED", company_id: companyId });
 */
export function emitLocalEvent(evt: WsMessage) {
  listeners.forEach((l) => {
    try { l(evt); } catch {}
  });
}

function clearTimers() {
  if (pingTimer) clearInterval(pingTimer);
  pingTimer = null;

  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
}

function scheduleReconnect(baseWsUrl: string) {
  if (!shouldReconnect || !isBrowserOnline()) return;

  // backoff: 1s, 2s, 4s, 8s... até 15s
  reconnectAttempt += 1;
  const delay = Math.min(15000, 1000 * Math.pow(2, reconnectAttempt - 1));

  reconnectTimer = setTimeout(() => {
    connectNotificationsWS(baseWsUrl);
  }, delay);
}

export async function connectNotificationsWS(baseWsUrl: string) {
  if (opening) return;
  installNotificationNetworkListeners(baseWsUrl);

  if (!isBrowserOnline()) {
    return;
  }

  const token = readStoredAccessToken();
  if (!token) return;

  // se token mudou, permite reconectar com token novo
  const tokenChanged = lastToken && lastToken !== token;

  // evita duplicar conexão, a menos que o token mudou
  if (
    ws &&
    !tokenChanged &&
    (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)
  ) {
    console.log('✅ WebSocket de notificações já conectado/conectando. Ignorando nova conexão.');
    return;
  }

  // ✅ CRITICAL: se token mudou ou existe conexão, fecha adequadamente antes de criar nova
  if (ws) {
    console.log('🔌 Fechando conexão WebSocket de notificações anterior');
    try {
      // Remover listeners para evitar chamadas durante o fechamento
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, tokenChanged ? 'Token mudou' : 'Nova conexão sendo criada');
      }
    } catch (error) {
      console.warn('Erro ao fechar conexão WebSocket anterior:', error);
    } finally {
      ws = null;
    }
  }

  shouldReconnect = true;
  lastToken = token;

  clearTimers();

  const epoch = ++openGeneration;
  opening = true;
  const identityEpoch = sessionIdentityEpoch();
  let ticket: string;
  try { ticket = await requestWsTicket('/api/v1/ws'); }
  catch { if (epoch === openGeneration) scheduleReconnect(baseWsUrl); return; }
  finally { if (epoch === openGeneration) opening = false; }
  if (identityEpoch !== sessionIdentityEpoch() || epoch !== openGeneration || !shouldReconnect || !readStoredAccessToken()) return;
  ws = new WebSocket(`${baseWsUrl}/api/v1/ws?ticket=${encodeURIComponent(ticket)}`);

  ws.onopen = () => {
    console.log("✅ WS conectado");
    reconnectAttempt = 0; // reset backoff

    // keep-alive
    pingTimer = setInterval(() => {
      try {
        if (ws && ws.readyState === WebSocket.OPEN) ws.send("ping");
      } catch {}
    }, 25000);
  };

  ws.onmessage = (event) => {
    try {
      const msg: WsMessage = JSON.parse(event.data);

      // 🔥 dispara para todos que estiverem ouvindo
      listeners.forEach((l) => {
        try { l(msg); } catch {}
      });
    } catch {
      // se não for JSON (ex: "pong"), ignora silenciosamente
      // console.log("WS raw:", event.data);
    }
  };

  ws.onerror = () => {
    // Silencioso — evita expor URL/token no console do browser
  };

  ws.onclose = (event) => {
    console.log(`⚠️ WS desconectou (code=${event.code})`);
    ws = null;
    clearTimers();

    // se foi logout ou você explicitamente pediu para não reconectar, não reconecta
    if (!shouldReconnect) return;

    // se não tem token, não reconecta
    if (!readStoredAccessToken()) return;

    scheduleReconnect(baseWsUrl);
  };
}

export function disconnectWS() {
  openGeneration += 1;
  opening = false;
  shouldReconnect = false; // impede que o onclose reagende reconexão
  lastToken = null;
  clearTimers();

  try { ws?.close(); } catch {}
  ws = null;
}
